import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import CustomTable, { Column } from '../components/CustomTable'; // Import the new CustomTable
import { InventoryInterface } from '../types';
import moment from 'moment';
import ProductInventoryModal from '../components/ProductInventoryModal';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import { useNavigate } from 'react-router';
import * as routes from '../utils/routes';
import InventoryIcon from '../assets/svgs/InventoryIcon';
import ReactGA from 'react-ga4';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

export default function Inventory() {
	const [inventory, setInventory] = useState<InventoryInterface[]>([]);
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedVariant, setSelectedVariant] = useState<InventoryInterface | null>(null);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	const navigate = useNavigate();

	// Helper function to get cell class name based on stock level
	const getStockLevelClass = (params: any) => {
		if (!params) return '';

		const total = params.total_quantity === null ? 0 : params.total_quantity;
		const minLevel = params.min_stock_level || 0;

		if (total === 0) {
			return 'out-of-stock';
		}
		if (total < minLevel) {
			return 'low-stock';
		}
		return '';
	};

	const columns: Column[] = [
		{
			field: 'variant_name',
			headerName: 'Variant',
			minWidth: 260,
			headerClassName: 'table-header',
			cellClassName: (params) => {
				const stockClass = getStockLevelClass(params);
				return `font-medium text-gray-900 ${stockClass}`;
			},
			renderCell: (params) => {
				const variant = params.row;
				return (
					<div className="flex flex-col">
						<span className="text-gray-900  font-semibold" style={{ fontWeight: 'bold' }}>
							{variant.product?.name}
						</span>
						<span className="text-gray-500 text-sm">{variant.variant_name}</span>
					</div>
				);
			},
			flex: 2,
		},
		{
			field: 'price',
			headerName: 'Price',
			minWidth: 120,
			headerClassName: 'table-header',
			cellClassName: (params) => {
				const stockClass = getStockLevelClass(params);
				return `text-gray-500 ${stockClass}`;
			},
			flex: 1,
			valueGetter: (params) => `${currency} ${Number(params.value).toFixed(2)}`,
		},
		{
			field: 'total_quantity',
			headerName: 'Quantity',
			minWidth: 130,
			headerClassName: 'table-header',
			cellClassName: (params) => {
				const stockClass = getStockLevelClass(params);
				const badgeClass =
					params.total_quantity === 0
						? 'out-of-stock-text'
						: params.total_quantity < params.min_stock_level
						? 'low-stock-text'
						: '';
				return `text-gray-500 ${stockClass} ${badgeClass}`;
			},
			flex: 1,
			valueGetter: (params) => (params.value === null ? 0 : params.value),
		},
		{
			field: 'last_restocked_date',
			headerName: 'Last Restocked',
			minWidth: 170,
			headerClassName: 'table-header',
			cellClassName: (params) => {
				const stockClass = getStockLevelClass(params);
				return `text-gray-500 ${stockClass}`;
			},
			flex: 1,
			renderCell: (params) => (params.value ? moment(params.value).format('Do MMM YYYY') : '-'),
		},
	];

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/inventory',
			title: 'Variant Inventory Tab',
		});

		setFetchState('loading');
		apiClient
			.get('/inventory')
			.then((response: any) => {
				setFetchState('success');
				setInventory(response.data);
				ReactGA.event({
					category: 'Inventory',
					action: 'Fetched Inventory',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.log('error fetching inventory', error);
				setFetchState('failed');
				ReactGA.event({
					category: 'Inventory',
					action: 'Fetch Inventory Failed',
					label: 'Fetch Failed',
				});
			});
	}, []);

	const hideSidePanel = (inventoryData: any = null) => {
		console.log('inventoryData', inventoryData);
		setIsEditPanelVisible(false);
		setSelectedVariant(null);

		if (inventoryData && inventoryData.variant_id) {
			// Use the updated inventory data returned from the backend
			setInventory(
				inventory.map((variant: InventoryInterface) => {
					if (variant.variant_id === inventoryData.variant_id) {
						const updatedVariant = {
							...variant,
							total_quantity: inventoryData.total_quantity,
							last_restocked_date: inventoryData.last_restocked_date || moment().format('YYYY-MM-DD'),
						};

						ReactGA.event({
							category: 'Inventory',
							action: 'Updated Inventory Item',
							label: `Variant ID: ${variant.variant_id}`,
						});

						return updatedVariant;
					}

					return variant;
				})
			);
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed Inventory Panel',
				label: 'No Changes',
			});
		}
	};

	const handleRowClick = (params: { row: InventoryInterface }) => {
		const variant = params.row;
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Inventory Row',
			label: `Variant ID: ${variant.variant_id}`,
		});
		setSelectedVariant(variant);
		setIsEditPanelVisible(true);
	};

	const navigateToVariants = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Navigated to Variants Page',
			label: 'From Empty Inventory',
		});
		navigate(routes.PRODUCTS);
	};

	return (
		<Wrapper>
			<Header title="Inventory" description="Create edit or delete inventory items here" />
			<>
				<CustomTable
					objects={inventory}
					columns={columns}
					onRowClick={(params) => handleRowClick(params)}
					emptyStateComponent={() => (
						<EmptyState
							handleButtonClick={navigateToVariants}
							title="No Variants Yet"
							description="You'll need to first create a product. Once added, you can manage and track your variants on this page"
							buttonText="Go To Variants Page"
							icon={<InventoryIcon width={50} height={50} />}
						/>
					)}
					fetchState={fetchState}
					sx={{ marginTop: '20px' }}
					initialRowsPerPage={25}
					// Use variant_id as the unique identifier instead of id
					getRowId={(row) => row.variant_id}
				/>
				<ProductInventoryModal
					isVisible={isEditPanelVisible}
					handleHidePanel={hideSidePanel}
					variant={selectedVariant}
				/>
			</>
		</Wrapper>
	);
}
